// BaseFormV2 — RHF engine on @refinedev/react-hook-form reproducing the v3
// BaseForm pipeline (src/components/common/baseCrud/BaseForm.tsx +
// src/hooks/dataHelpers/overrides/useCustomForm.ts):
//   resolve → hydrate (query / resourceData fast-path) → processInitialValues
//   → validate (onSubmit, revalidate onChange) → processFormBeforeSubmit
//   → mutate → invalidate (Refine defaults) → successCallback → redirect.
// Semantics ported, not code — the antd Form surface is replaced by RHF.
//
// Cache freshness: v3's `invalidates: [] + in-place cache write` optimization
// (useCustomCacheUpdate) was ported here initially and REMOVED (APL-11850
// cachefix). The write never matched the query keys @refinedev/react-table
// registers: useList keys are `['data', <dp>, <resource>, 'list', { ...meta,
// filters, hasPagination, pagination, sorters }]`, and the shared helper
// (getKeysFromPreviousQueries) bails to `[]` unless the key-to-match carries a
// `filters` object — which the form never passed. Net effect: the write was a
// silent no-op and `invalidates: []` suppressed Refine's own invalidation, so
// lists stayed stale until a manual reload. We now rely on Refine's standard
// invalidation (create → list+many, update → list+many+detail): a refetch on
// return-to-list is cheap, always key-correct, and shape-agnostic.
import { BaseKey, BaseRecord, HttpError } from '@refinedev/core';
import { useForm, type UseFormProps as UseRefineHookFormProps } from '@refinedev/react-hook-form';
import { get, set } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import type { Control, DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form';

import { scrollToFirstFormError } from '@/lib/commonHelpers';
import { logRedactedError } from '@/lib/telemetry/logError';

export interface UseAPFormProps<TVariables extends FieldValues = FieldValues> {
  resource: string;
  /** Singular envelope key in the JSON:API response (`data[resourceKey].data`); defaults to the singularized resource. */
  resourceKey?: string;
  mode: 'create' | 'edit';
  id?: BaseKey;
  /** Hydration fast-path: skips the detail query and hydrates from this record (v3 `resourceData`). */
  resourceData?: Record<string, any> | null;
  defaultValues?: DefaultValues<TVariables>;
  /** Reshape the fetched record before it hydrates the form (v3 contract). */
  processInitialValues?: (initialValues: any) => any;
  /** Reshape validated values before the mutation (v3 contract — receives values, hydrated initial values, and the RHF form). */
  processFormBeforeSubmit?: (
    formData: TVariables,
    initialValues?: any,
    form?: UseFormReturn<TVariables>
  ) => any | Promise<any>;
  successCallback?: (data: any) => void;
  cancelCallback?: () => void;
  /**
   * Payload pick strategy (runs BEFORE processFormBeforeSubmit):
   * - 'registered' (default) — submit only RHF-managed names: top-level
   *   registered scalars, field-array roots WHOLE (rows incl. id/_destroy keys
   *   from form state), deep-registered scalar paths reassembled to ONLY that
   *   nested key (`custom_fields.show_notice` → `{ custom_fields: { show_notice } }`).
   *   Hydrated-but-unmanaged record keys never ride back on the wire.
   *   ⚠ jsonb ledger rule: a reassembled PARTIAL hash over a REPLACE-semantics
   *   BE column loses the unregistered keys — merge initialValues∪edited in
   *   processFormBeforeSubmit (see MIGRATION.md § Form submit-pattern & jsonb ledger).
   * - 'all' — legacy passthrough: full RHF form state (hydrated record included).
   */
  submitFields?: 'registered' | 'all';
  /** Unmanaged top-level keys the BE still requires — copied from form state
   *  alongside the registered pick (e.g. setValue-managed `*_attributes` arrays). */
  alsoSubmit?: string[];
  /** `false` → redirect 'list' after save (v3 default); `true` → stay on the form. */
  keepFormOpenAfterSave?: boolean;
  /** Extra overrides merged last into refineCoreProps. */
  refineCoreProps?: UseRefineHookFormProps<BaseRecord, HttpError, TVariables>['refineCoreProps'];
  /** Extra react-hook-form options (resolver, mode overrides…). */
  rhfProps?: Omit<UseRefineHookFormProps<BaseRecord, HttpError, TVariables>, 'refineCoreProps'>;
}

const singularize = (resource: string) =>
  resource.endsWith('s') ? resource.slice(0, -1) : resource;

const isIndexSegment = (segment: string) => /^\d+$/.test(segment);

/**
 * The 'registered' pick (submitFields default). Exported for unit tests.
 *
 * - `arrayNames` (RHF `_names.array`, useFieldArray roots) → the TOP-LEVEL root
 *   is picked WHOLE from form state (rows keep id/_destroy/unrendered keys).
 * - Registered paths containing an index segment (row fields registered
 *   without useFieldArray) → their top-level root is also picked whole.
 * - Top-level registered scalars → copied as-is.
 * - Deep-registered scalar paths → reassembled so ONLY that nested key ships.
 * - `alsoSubmit` top-level keys → copied from form state when present.
 */
export const pickRegisteredValues = (
  values: Record<string, any>,
  mountNames: Iterable<string>,
  arrayNames: Iterable<string>,
  alsoSubmit?: string[]
): Record<string, any> => {
  const picked: Record<string, any> = {};
  const wholeRoots = new Set<string>();

  for (const arrayName of arrayNames) {
    wholeRoots.add(String(arrayName).split('.')[0]);
  }

  for (const path of mountNames) {
    const segments = String(path).split('.');
    const root = segments[0];
    if (wholeRoots.has(root)) continue;
    if (segments.length === 1) {
      if (root in values) picked[root] = values[root];
      continue;
    }
    if (segments.some(isIndexSegment)) {
      wholeRoots.add(root);
      continue;
    }
    const value = get(values, path);
    if (value !== undefined) set(picked, path, value);
  }

  for (const root of wholeRoots) {
    if (root in values) picked[root] = values[root];
  }

  for (const key of alsoSubmit ?? []) {
    if (key in values) picked[key] = values[key];
  }

  return picked;
};

export const useAPForm = <TVariables extends FieldValues = FieldValues>(
  props: UseAPFormProps<TVariables>
) => {
  const {
    resource,
    resourceKey = singularize(props.resource),
    mode,
    id,
    resourceData,
    defaultValues,
    processInitialValues,
    processFormBeforeSubmit,
    successCallback,
    cancelCallback,
    submitFields = 'registered',
    alsoSubmit,
    keepFormOpenAfterSave = false,
    refineCoreProps,
    rhfProps,
  } = props;

  const [initialValues, setInitialValues] = useState<any>(resourceData);
  const [isHydrating, setIsHydrating] = useState<boolean>(mode === 'edit');

  const formReturn = useForm<BaseRecord, HttpError, TVariables>({
    // v3 parity: validate on submit, then revalidate touched fields on change.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
    warnWhenUnsavedChanges: true,
    ...rhfProps,
    refineCoreProps: {
      resource,
      action: mode,
      ...(id !== undefined ? { id } : {}),
      redirect: keepFormOpenAfterSave ? false : 'list',
      // resourceData fast-path: hydrate locally, skip the detail query (v3 parity).
      ...(resourceData ? { queryOptions: { enabled: false } } : {}),
      // No `invalidates` override — Refine's defaults keep every list/detail
      // fresh on return (see the cache-freshness note in the file header).
      ...refineCoreProps,
    },
  });

  const { refineCore, saveButtonProps, ...form } = formReturn;
  const query = refineCore.query ?? refineCore.queryResult;

  // Hydration order (v3 parity): resourceData → envelope under resourceKey → raw payload.
  const record = resourceData ?? query?.data?.data?.[resourceKey] ?? query?.data?.data;
  // Guarded by record id, not reference: an inline `resourceData={{…}}` literal gets a
  // new identity every render, and reference-guarded reset() would loop; a refetch of the
  // same id must NOT clobber in-progress user edits either.
  const hydratedIdRef = useRef<any>(undefined);

  useEffect(() => {
    if (record?.id && hydratedIdRef.current !== record.id) {
      hydratedIdRef.current = record.id;
      const processed = processInitialValues ? processInitialValues(record) : record;
      form.reset(processed as DefaultValues<TVariables>);
      setInitialValues(processed);
      setIsHydrating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  const formLoading = refineCore.formLoading;
  const showSkeleton = mode === 'edit' && !resourceData && (query?.isLoading || isHydrating);

  // Validate → pick (submitFields) → preprocess → mutate → success callback.
  // Mutation/network errors surface through Refine's notificationProvider;
  // processing errors are logged (v3 parity) and abort the submit. Resolves the
  // mutation response on success, undefined when validation / preprocessing /
  // the mutation failed (RHF's handleSubmit discards the callback's return
  // value, hence the outer capture).
  const submit = async (): Promise<any> => {
    let outcome: any = undefined;
    await form.handleSubmit(
      async (values) => {
        try {
          let finalValues: any = values;
          if (submitFields === 'registered') {
            const names = (form.control as Control)._names;
            finalValues = pickRegisteredValues(values, names.mount, names.array, alsoSubmit);
          }
          if (processFormBeforeSubmit) {
            finalValues = await processFormBeforeSubmit(finalValues, initialValues, form);
          }
          const result = await refineCore.onFinish(finalValues);
          successCallback?.(result);
          outcome = result ?? true;
        } catch (error) {
          // Redacted — mutation errors carry the response body (possibly PII).
          logRedactedError('BaseFormV2 submit failed', error);
        }
      },
      // P2-76: a failed validation otherwise leaves Save looking dead when the
      // first invalid field is scrolled off-screen — scroll+focus it (shadcn
      // FormMessage's data-slot="form-message" marker, kit-generic).
      () => scrollToFirstFormError()
    )();
    return outcome;
  };

  // v3 handleCancelClick parity: restore hydrated values, then notify.
  const cancel = () => {
    form.reset((initialValues ?? defaultValues ?? {}) as DefaultValues<TVariables>);
    cancelCallback?.();
  };

  return {
    /** RHF methods — spread into shadcn's `<Form {...form}>` provider. */
    form: form as UseFormReturn<TVariables>,
    refineCore,
    initialValues,
    isHydrating,
    showSkeleton,
    formLoading,
    submit,
    cancel,
    saveButtonProps: { ...saveButtonProps, onClick: submit },
  };
};

export type UseAPFormReturn<TVariables extends FieldValues = FieldValues> = ReturnType<
  typeof useAPForm<TVariables>
>;
